import type { PlayEventFormat } from '../types/playEvent';
import { effectiveParticipantSeed, resolveSeedsFromRatings } from './playerRating';

export interface DrawParticipant {
  id: string;
  seed?: number | null;
  playerRating?: number | null;
}

export interface DrawMatchSlot {
  roundNumber: number;
  matchIndex: number;
  sideAId?: string | null;
  sideBId?: string | null;
  isBye?: boolean;
}

function nextPowerOfTwo(n: number): number {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

function sortBySeed(participants: DrawParticipant[]): DrawParticipant[] {
  const computedSeeds = resolveSeedsFromRatings(participants);
  return [...participants].sort((a, b) => {
    const sa = effectiveParticipantSeed(a, computedSeeds);
    const sb = effectiveParticipantSeed(b, computedSeeds);
    if (sa !== sb) return sa - sb;
    return a.id.localeCompare(b.id);
  });
}

/** Round-robin circle method — returns round pairings as participant id pairs */
export function generateRoundRobinRounds(participantIds: string[]): Array<Array<[string, string]>> {
  const ids = [...participantIds];
  if (ids.length < 2) return [];
  if (ids.length % 2 === 1) ids.push('__BYE__');
  const n = ids.length;
  const rounds: Array<Array<[string, string]>> = [];
  const fixed = ids[0];
  const rotating = ids.slice(1);

  for (let r = 0; r < n - 1; r++) {
    const order = [fixed, ...rotating];
    const pairs: Array<[string, string]> = [];
    for (let i = 0; i < n / 2; i++) {
      const a = order[i];
      const b = order[n - 1 - i];
      if (a !== '__BYE__' && b !== '__BYE__') pairs.push([a, b]);
    }
    rounds.push(pairs);
    rotating.unshift(rotating.pop()!);
  }
  return rounds;
}

export function generateSingleEliminationBracket(
  participants: DrawParticipant[]
): DrawMatchSlot[] {
  const sorted = sortBySeed(participants);
  const bracketSize = nextPowerOfTwo(sorted.length);
  const slots: (string | null)[] = new Array(bracketSize).fill(null);
  for (let i = 0; i < sorted.length; i++) {
    slots[i] = sorted[i].id;
  }

  const matches: DrawMatchSlot[] = [];
  let matchIndex = 0;
  const roundCount = Math.log2(bracketSize);
  for (let round = 1; round <= roundCount; round++) {
    const matchesInRound = bracketSize / Math.pow(2, round);
    for (let m = 0; m < matchesInRound; m++) {
      if (round === 1) {
        const a = slots[m * 2] ?? null;
        const b = slots[m * 2 + 1] ?? null;
        matches.push({
          roundNumber: round,
          matchIndex: matchIndex++,
          sideAId: a,
          sideBId: b,
          isBye: Boolean(a && !b) || Boolean(!a && b),
        });
      } else {
        matches.push({
          roundNumber: round,
          matchIndex: matchIndex++,
          sideAId: null,
          sideBId: null,
        });
      }
    }
  }
  return matches;
}

export function packRoundRobinIntoCourtRounds(
  pairRounds: Array<Array<[string, string]>>,
  courtCount: number
): Array<{ scheduleRound: number; pairs: Array<[string, string]> }> {
  if (courtCount < 1) return [];

  const packed: Array<{ scheduleRound: number; pairs: Array<[string, string]> }> = [];
  let scheduleRound = 1;
  for (const rr of pairRounds) {
    for (let i = 0; i < rr.length; i += courtCount) {
      packed.push({ scheduleRound: scheduleRound++, pairs: rr.slice(i, i + courtCount) });
    }
  }
  return packed;
}

export function formatRequiresPreformedTeams(
  format: PlayEventFormat,
  side: 'singles' | 'doubles' | 'mixed'
): boolean {
  if (side === 'singles') return false;
  return format === 'round_robin' || format === 'single_elimination';
}
